import classNames from 'classnames';
import type { Delta, Op } from 'quill/core';
import type Quill from 'quill/core';
import React, { useEffect, useRef, useState } from 'react';
import * as styles from './RichTextDescriptionControl.module.scss';
import RichTextDescriptionEditor from './RichTextDescriptionEditor';

const MAX_HEIGHT = 100;
const HEIGHT_ANIMATION_DURATION = 150;

interface RichTextDescriptionControlProps {
  error?: string;
  label?: React.ReactNode;
  name: string;
  value: Delta | Op[];
  placeholder?: string;
  disabled?: boolean;
  onChange: (value: { target: { value: Op[]; name: string } }) => void;
}

const RichTextDescriptionControl = ({
  error,
  label,
  name,
  value,
  onChange,
  placeholder,
  disabled,
}: RichTextDescriptionControlProps) => {
  const quillRef = useRef<Quill | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [maxHeight, setMaxHeight] = useState(MAX_HEIGHT);
  const contentRef = useRef(value);

  useEffect(() => {
    if (!quillRef.current || !isFocused) return;
    // CSS doesn't support animating from/to `auto`/`none`, so during the
    // animation we use the content height as the max value and remove the
    // restriction after the animation to make it auto grow to fit the content.
    setMaxHeight(quillRef.current.root.scrollHeight);
    setTimeout(() => setIsExpanded(true), HEIGHT_ANIMATION_DURATION);
  }, [isFocused]);

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <label
        onClick={() => {
          if (quillRef.current) quillRef.current.focus();
        }}
      >
        {!!label && <div className={styles.label}>{label}</div>}
      </label>
      <div
        className={classNames(styles.editorContainer, {
          [styles.disabled]: disabled,
          [styles.error]: error,
        })}
      >
        <div
          className={classNames(styles.editor, {
            [styles.expanded]: isExpanded,
          })}
          style={
            isExpanded
              ? undefined
              : {
                  maxHeight,
                  transition: `max-height ${HEIGHT_ANIMATION_DURATION}ms`,
                }
          }
          onFocus={() => setIsFocused(true)}
        >
          <RichTextDescriptionEditor
            noTitleLine
            contentPlaceholder={placeholder}
            namespace="topicUserDescription"
            readOnly={!!disabled}
            onMount={(quill) => {
              quillRef.current = quill;
            }}
            defaultValue={contentRef.current}
            onTextChange={(text) => {
              onChange({
                target: {
                  name,
                  value: text,
                },
              });
            }}
          />
        </div>
      </div>
      {!disabled && (
        <div className={styles.tip}>
          <strong>Tip:</strong> You can add lists, format text, and mention
          users or posts. Type ‘/’ to see all options
        </div>
      )}
    </>
  );
};

export default RichTextDescriptionControl;
